import {Action, DataStatus, openDeleteDialog} from 'platform/components';
import {VStack} from 'platform/foundation';

import {
  InterestAssignmentConditionResponseBody,
  useRemoveInterestAssignmentConditionMutation,
  useReorderInterestAssignmentConditionsMutation,
} from '@dms/api/core';
import {DraggableCard, SortableCardList} from '@dms/features/dnd';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsSection} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {buildArray, composePath, RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

interface AssignmentConditionListProps extends RequiredTestIdProps {
  conditions: InterestAssignmentConditionResponseBody[];
}

const CONTAINER_ID = 'sales-leads-assignment-conditions';

export function AssignmentConditionList(props: AssignmentConditionListProps) {
  const navigate = useNavigate();
  const [reorderConditions] = useReorderInterestAssignmentConditionsMutation();
  const [removeCondition] = useRemoveInterestAssignmentConditionMutation();

  const handleEdit = (conditionId: string) => {
    navigate(composePath(settingsRoutes.salesLeadsAssignmentEdit, {params: {id: conditionId}}));
  };

  const handleDelete = (condition: InterestAssignmentConditionResponseBody) => {
    openDeleteDialog({
      text: i18n.t('entity.interest.labels.deleteConditionDialogText'),
      'data-testid': testIds.settings.salesLeadsAssignment('deleteConditionConfirmDialog'),
      onConfirm: () => {
        removeCondition({conditionId: condition.id}).unwrap().catch(handleApiError);
      },
    });
  };

  const isEmpty = props.conditions.length === 0;

  return (
    <DataStatus isEmpty={isEmpty} data-testid={suffixTestId('list', props)}>
      <SettingsSection>
        <VStack spacing={4} align="flex-start">
          <SortableCardList<InterestAssignmentConditionResponseBody>
            withHandle
            disableDropAnimation
            containerId={CONTAINER_ID}
            items={props.conditions}
            onReorder={(reorderedItems) => {
              reorderConditions({
                reorderConditionsRequestBody: {
                  conditions: reorderedItems.map((item, index) => ({
                    conditionId: item.id,
                    rank: index,
                  })),
                },
              })
                .unwrap()
                .catch(handleApiError);
            }}
            renderCard={(condition, index, handleProps) => (
              <DraggableCard
                title={condition.name}
                handleProps={handleProps}
                variant="inlineGrey"
                data-testid={suffixTestId(`condition-${index}`, props)}
                actions={buildArray<Action>()
                  .add({
                    type: 'iconButton',
                    icon: 'image/edit',
                    onClick: () => handleEdit(condition.id),
                    'data-testid': testIds.settings.salesLeadsAssignment(`editCondition-${index}`),
                  })
                  .add({
                    type: 'iconButton',
                    icon: 'action/delete',
                    severity: 'danger',
                    onClick: () => handleDelete(condition),
                    'data-testid': testIds.settings.salesLeadsAssignment(
                      `deleteCondition-${index}`
                    ),
                  })}
              />
            )}
          />
        </VStack>
      </SettingsSection>
    </DataStatus>
  );
}
